// Currency display — backend still charges in EUR via Stripe.
// FX rates fetched from `fx_rates` table (refreshed weekly), cached in localStorage 24h.
// Mobile-money payment links are pre-priced in local currency by the admin.
// State lives in src/lib/pricing/currency.ts (Zustand) so all consumers share it.
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFxRates } from "@/hooks/useFxRates";
import { useCurrencyStore, CURRENCIES, type CurrencyCode, formatMinor } from "@/lib/pricing/currency";

export { CURRENCIES, formatMinor };
export type { CurrencyCode };

const COUNTRY_KEY = "bp.country";

export function defaultForCountry(country: string | null | undefined): CurrencyCode | null {
  if (!country) return null;
  const c = country.toUpperCase();
  if (c === "GH") return "GHS";
  if (["CM", "CG", "GA", "TD", "CF", "GQ"].includes(c)) return "XAF";
  if (["CI", "SN", "ML", "BF", "TG", "BJ", "NE", "GW"].includes(c)) return "XOF";
  if (c === "GN") return "GNF";
  if (c === "NG") return "NGN";
  if (c === "KE") return "KES";
  if (c === "BR") return "BRL";
  if (c === "US") return "USD";
  if (c === "GB" || c === "UK") return "USD";
  if (c === "MA") return "MAD";
  if (c === "MX") return "MXN";
  if (c === "AR") return "ARS";
  if (c === "EG") return "EGP";
  if (c === "ZA") return "ZAR";
  if (c === "CO") return "COP";
  if (c === "ID") return "IDR";
  if (c === "PH") return "PHP";
  if (c === "VN") return "VND";
  if (c === "IN") return "INR";
  if (c === "AU" || c === "CA") return "USD";
  if (["FR", "DE", "ES", "IT", "PT", "NL", "BE", "IE", "AT", "FI", "GR"].includes(c)) return "EUR";
  return null;
}

function defaultForLocale(lang: string): CurrencyCode {
  if (typeof window !== "undefined") {
    try {
      const country =
        window.localStorage.getItem("bp.country.manual") ||
        window.sessionStorage.getItem(COUNTRY_KEY) ||
        window.localStorage.getItem(COUNTRY_KEY);
      const fromCountry = defaultForCountry(country);
      if (fromCountry) return fromCountry;
    } catch {/* noop */}
  }
  const l = (lang || "en").slice(0, 2).toLowerCase();
  if (l === "pt") return "BRL";
  return "EUR"; // EUR is the canonical anchor; switcher allows override
}

export function getStoredCurrency(): CurrencyCode | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem("bp.currency");
  return v && v in CURRENCIES ? (v as CurrencyCode) : null;
}

export function formatPrice(amountEur: number, c: CurrencyCode, rate: number) {
  const { symbol } = CURRENCIES[c];
  const v = Math.round(amountEur * rate);
  return `${symbol}${v.toLocaleString()}`;
}

export function useCurrency() {
  const { i18n } = useTranslation();
  const rates = useFxRates();
  const currency = useCurrencyStore((s) => s.currency);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);

  // On first mount, if no stored choice yet, pick a sensible default for locale.
  useEffect(() => {
    if (!getStoredCurrency()) {
      const def = defaultForLocale(i18n.language);
      if (def !== currency) setCurrency(def);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rate = rates[currency] ?? 1;

  return {
    currency,
    setCurrency,
    format: (amountEur: number) => formatPrice(amountEur, currency, rate),
    supported: Object.keys(CURRENCIES) as CurrencyCode[],
    rate,
  };
}
